import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Group, SimpleGrid, Stack, Table, Tabs, Text, TextInput, Textarea } from '@mantine/core';
import { PageHeader } from '../components/PageHeader';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

type RoleRow = { id: string; name: string; description?: string | null; image_url?: string | null };
type RessortRow = { id: string; name: string };

function linesToList(s: string): string[] {
  return s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
}

function listToLines(v: unknown): string {
  if (!Array.isArray(v)) return '';
  return v.map((x) => String(x)).join('\n');
}

export function ConfigPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string | null>('calendar');

  useEffect(() => {
    document.title = 'Einstellungen – Musical!Kultur Admin';
  }, []);

  const cfgQ = useQuery({
    queryKey: ['config', 'admin'],
    queryFn: async (): Promise<Record<string, unknown>> => {
      const keys = ['event_types', 'ghost_musik_akt1', 'ghost_musik_akt2'];
      const { data, error } = await supabase.from('config').select('key,value').in('key', keys);
      if (error) throw error;
      const out: Record<string, unknown> = {};
      for (const row of (data ?? []) as Array<{ key: string; value: unknown }>) {
        out[row.key] = row.value;
      }
      return out;
    },
  });

  const [draftEventTypesText, setDraftEventTypesText] = useState<string | null>(null);
  const [draftAct1Text, setDraftAct1Text] = useState<string | null>(null);
  const [draftAct2Text, setDraftAct2Text] = useState<string | null>(null);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: async (): Promise<RoleRow[]> => {
      const { data, error } = await supabase.from('roles').select('id,name,description,image_url').order('name');
      if (error) throw error;
      return (data ?? []) as RoleRow[];
    },
  });

  const ressortsQ = useQuery({
    queryKey: ['ressorts'],
    queryFn: async (): Promise<RessortRow[]> => {
      const { data, error } = await supabase.from('ressorts').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as RessortRow[];
    },
  });

  const [draftRoleNames, setDraftRoleNames] = useState<Record<string, string>>({});
  const [draftRoleDescriptions, setDraftRoleDescriptions] = useState<Record<string, string>>({});
  const [draftRessortNames, setDraftRessortNames] = useState<Record<string, string>>({});
  const [newRoleName, setNewRoleName] = useState('');
  const [newRessortName, setNewRessortName] = useState('');

  const eventTypesText = draftEventTypesText ?? listToLines(cfgQ.data?.['event_types']);
  const act1Text = draftAct1Text ?? listToLines(cfgQ.data?.['ghost_musik_akt1']);
  const act2Text = draftAct2Text ?? listToLines(cfgQ.data?.['ghost_musik_akt2']);

  const saveM = useMutation({
    mutationFn: async () => {
      const payload = [
        { key: 'event_types', value: linesToList(eventTypesText) },
        { key: 'ghost_musik_akt1', value: linesToList(act1Text) },
        { key: 'ghost_musik_akt2', value: linesToList(act2Text) },
      ];
      const { error } = await supabase.from('config').upsert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      setDraftEventTypesText(null);
      setDraftAct1Text(null);
      setDraftAct2Text(null);
      notifications.show({ color: 'teal', title: 'Config', message: 'Gespeichert.' });
      await qc.invalidateQueries({ queryKey: ['config'] });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Speichern fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const saveRolesRessortsM = useMutation({
    mutationFn: async () => {
      const tasks: Array<Promise<void>> = [];

      // Roles: update names and descriptions by ID (UUID stays stable)
      for (const r of rolesQ.data ?? []) {
        const nextName = (draftRoleNames[r.id] ?? r.name).trim();
        const nextDesc = (draftRoleDescriptions[r.id] ?? (r.description ?? '')).trim();
        if ((nextName && nextName !== r.name) || (nextDesc && nextDesc !== (r.description ?? ''))) {
          tasks.push(
            (async () => {
              const { error } = await supabase.from('roles').update({ name: nextName, description: nextDesc }).eq('id', r.id);
              if (error) throw error;
            })(),
          );
        }
      }

      // Ressorts: update names by ID (UUID stays stable)
      for (const r of ressortsQ.data ?? []) {
        const nextName = (draftRessortNames[r.id] ?? r.name).trim();
        if (!nextName || nextName === r.name) continue;
        tasks.push(
          (async () => {
            const { error } = await supabase.from('ressorts').update({ name: nextName }).eq('id', r.id);
            if (error) throw error;
          })(),
        );
      }

      const addRole = newRoleName.trim();
      if (addRole) {
        tasks.push(
          (async () => {
            const { error } = await supabase.from('roles').insert({ name: addRole });
            if (error) throw error;
          })(),
        );
      }

      const addRessort = newRessortName.trim();
      if (addRessort) {
        tasks.push(
          (async () => {
            const { error } = await supabase.from('ressorts').insert({ name: addRessort });
            if (error) throw error;
          })(),
        );
      }

      await Promise.all(tasks);
    },
    onSuccess: async () => {
      setNewRoleName('');
      setNewRessortName('');
      setDraftRoleNames({});
      setDraftRessortNames({});
      notifications.show({ color: 'teal', title: 'Einstellungen', message: 'Gespeichert.' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['roles'] }),
        qc.invalidateQueries({ queryKey: ['ressorts'] }),
      ]);
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Speichern fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const rolesChanged = useMemo(() => {
    const list = rolesQ.data ?? [];
    for (const r of list) {
      const nextName = (draftRoleNames[r.id] ?? r.name).trim();
      if (nextName && nextName !== r.name) return true;
    }
    return false;
  }, [draftRoleNames, rolesQ.data]);

  const ressortsChanged = useMemo(() => {
    const list = ressortsQ.data ?? [];
    for (const r of list) {
      const nextName = (draftRessortNames[r.id] ?? r.name).trim();
      if (nextName && nextName !== r.name) return true;
    }
    return false;
  }, [draftRessortNames, ressortsQ.data]);

  const addPending = newRoleName.trim().length > 0 || newRessortName.trim().length > 0;

  return (
    <Stack gap="md" className="contrast-page config-page">
      <PageHeader
        title="Konfiguration"
        description="Kalender-Typen, Songs, Rollen und Ressorts."
        loading={cfgQ.isLoading || rolesQ.isLoading || ressortsQ.isLoading}
      />

      <Card
        className="contrast-surface"
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
          <Tabs className="contrast-tabs" value={tab} onChange={setTab} variant="outline" color="brand">
            <Tabs.List>
              <Tabs.Tab value="calendar">Kalender</Tabs.Tab>
              <Tabs.Tab value="songs">Songs</Tabs.Tab>
              <Tabs.Tab value="access">Rollen & Ressorts</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="calendar" pt="md">
              <Textarea
                label="Kalender-Typen (1 pro Zeile)"
                value={eventTypesText}
                onChange={(e) => setDraftEventTypesText(e.currentTarget.value)}
                autosize
                minRows={10}
            />
          </Tabs.Panel>

            <Tabs.Panel value="songs" pt="md">
              <Stack gap="sm">
                <Textarea
                  label="Ghost Musik Akt 1 (1 pro Zeile, z.B. 'NR. 1: TITLE')"
                  value={act1Text}
                  onChange={(e) => setDraftAct1Text(e.currentTarget.value)}
                  autosize
                  minRows={10}
                />
                <Textarea
                  label="Ghost Musik Akt 2 (1 pro Zeile)"
                  value={act2Text}
                  onChange={(e) => setDraftAct2Text(e.currentTarget.value)}
                  autosize
                  minRows={10}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="access" pt="md">
              <Stack gap="md">
                <Text c="dimmed" size="sm">
                  Du kannst Rollen und Ressorts umbenennen oder neue hinzufügen. Die UUID bleibt dabei unverändert
                  (wichtig, weil sie überall verknüpft ist).
                </Text>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Card
                    className="contrast-surface-soft"
                    radius="md"
                    p="md"
                    style={{
                      background: 'var(--app-surface)',
                      border: '1px solid var(--app-border)',
                    }}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text c="var(--color-text)" fw={700}>
                          Rollen
                        </Text>
                      </Group>

                      <Table className="contrast-table" withRowBorders={false} highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Beschreibung</Table.Th>
                            <Table.Th>Bild</Table.Th>
                            <Table.Th>ID</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {(rolesQ.data ?? []).map((r) => (
                            <Table.Tr key={r.id}>
                              <Table.Td style={{ minWidth: 200 }}>
                                <TextInput
                                  value={draftRoleNames[r.id] ?? r.name}
                                  onChange={(e) =>
                                    setDraftRoleNames((prev) => ({ ...prev, [r.id]: e.currentTarget.value }))
                                  }
                                />
                              </Table.Td>
                              <Table.Td style={{ minWidth: 300 }}>
                                <Textarea
                                  value={draftRoleDescriptions[r.id] ?? r.description ?? ''}
                                  onChange={(e) =>
                                    setDraftRoleDescriptions((prev) => ({ ...prev, [r.id]: e.currentTarget.value }))
                                  }
                                  autosize
                                  minRows={2}
                                />
                              </Table.Td>
                              <Table.Td>
                                <Group align="center" gap="sm">
                                  {r.image_url ? (
                                    <img src={r.image_url} alt="role" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                                  ) : (
                                    <div style={{ width: 48, height: 48, background: 'var(--app-border)', borderRadius: 6 }} />
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      try {
                                        const objectPath = `feed/roles/${r.id}/${Date.now()}_${f.name}`;
                                        const { error: upErr } = await supabase.storage
                                          .from('feed-attachments')
                                          .upload(objectPath, f, { upsert: true });
                                        if (upErr) throw upErr;
                                        const pub = supabase.storage.from('feed-attachments').getPublicUrl(objectPath).data.publicUrl;
                                        const { error: updateErr } = await supabase.from('roles').update({ image_url: pub }).eq('id', r.id);
                                        if (updateErr) throw updateErr;
                                        await qc.invalidateQueries({ queryKey: ['roles'] });
                                      } catch (err) {
                                        notifications.show({ color: 'red', title: 'Upload fehlgeschlagen', message: String(err) });
                                      } finally {
                                      }
                                    }}
                                  />
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {r.id}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>

                      <Group grow>
                        <TextInput
                          label="Neue Rolle"
                          placeholder="z.B. Ensemble"
                          value={newRoleName}
                          onChange={(e) => setNewRoleName(e.currentTarget.value)}
                        />
                      </Group>
                    </Stack>
                  </Card>

                  <Card
                    className="contrast-surface-soft"
                    radius="md"
                    p="md"
                    style={{
                      background: 'var(--app-surface)',
                      border: '1px solid var(--app-border)',
                    }}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text c="var(--color-text)" fw={700}>
                          Ressorts
                        </Text>
                      </Group>

                      <Table className="contrast-table" withRowBorders={false} highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>ID</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {(ressortsQ.data ?? []).map((r) => (
                            <Table.Tr key={r.id}>
                              <Table.Td>
                                <TextInput
                                  value={draftRessortNames[r.id] ?? r.name}
                                  onChange={(e) =>
                                    setDraftRessortNames((prev) => ({ ...prev, [r.id]: e.currentTarget.value }))
                                  }
                                />
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {r.id}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>

                      <Group grow>
                        <TextInput
                          label="Neues Ressort"
                          placeholder="z.B. Cast"
                          value={newRessortName}
                          onChange={(e) => setNewRessortName(e.currentTarget.value)}
                        />
                      </Group>
                    </Stack>
                  </Card>
                </SimpleGrid>
              </Stack>
            </Tabs.Panel>
          </Tabs>

          <Group justify="flex-end" mt="md">
            <Button
              onClick={() => {
                if (tab === 'access') {
                  saveRolesRessortsM.mutate();
                } else {
                  saveM.mutate();
                }
              }}
              loading={saveM.isPending || saveRolesRessortsM.isPending}
              disabled={
                tab === 'access'
                  ? !(rolesChanged || ressortsChanged || addPending)
                  : false
              }
            >
              Speichern
            </Button>
          </Group>
        </Card>
    </Stack>
  );
}
